import { useEffect, useRef, useState } from 'react'
import { Bell, Check, ChevronRight, Loader2, ShieldAlert, TriangleAlert, X } from 'lucide-react'
import { getOperationNotificationDuration, type NotificationTone } from '@/lib/operationNotifications'
import type { NotificationActions } from '@/lib/notificationActions'
import NotificationBody from '@/components/NotificationBody'
import { getTenantStorageScope } from '@/lib/tenantStorage'
import { getAuthToken } from '@/lib/tokenStorage'
import api from '@/lib/api'

type Props = {
  createdAt: number
  eventTime: boolean
  title: string
  body: string
  tone: NotificationTone
  hasJob: boolean
  approval?: boolean
  actions?: NotificationActions
  onOpen: () => void
  onDismiss: () => void
}

const appearances = {
  success: { icon: Check, color: 'text-panel-success', background: 'bg-panel-success/10', border: 'border-panel-success/50', label: 'Başarılı' },
  warning: { icon: TriangleAlert, color: 'text-panel-warning', background: 'bg-panel-warning/10', border: 'border-panel-warning/60', label: 'Uyarı' },
  error: { icon: TriangleAlert, color: 'text-panel-danger', background: 'bg-panel-danger/10', border: 'border-panel-danger/50', label: 'Hata' },
  info: { icon: Bell, color: 'text-panel-accent', background: 'bg-panel-accent/10', border: 'border-panel-accent/50', label: 'Bilgi' },
}

export default function NotificationToast({ createdAt, eventTime, title, body, tone, hasJob, approval = false, actions, onOpen, onDismiss }: Props) {
  const [reading, setReading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null)
  const attempted = useRef(false)
  const mounted = useRef(true)
  const scope = useRef(getTenantStorageScope())
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])
  const appearance = appearances[result ? result.ok ? 'success' : 'warning' : approval ? 'warning' : tone]
  const Icon = approval && !result ? ShieldAlert : appearance.icon
  const label = result ? result.ok ? 'Karar iletildi' : 'Kontrol gerekiyor' : approval ? 'Onay bekliyor' : appearance.label
  const { color, background, border } = appearance

  useEffect(() => {
    const duration = getOperationNotificationDuration()
    if (reading || approval || actions || duration === 0) return
    const timer = window.setTimeout(onDismiss, duration)
    return () => window.clearTimeout(timer)
  }, [reading, approval, actions, onDismiss])

  async function decide(action: string) {
    if (!actions || attempted.current || !actions.actions.some(item => item.action === action)) return
    if (action === 'review') { onOpen(); return }
    if (!getAuthToken() || getTenantStorageScope() !== scope.current) { onDismiss(); return }
    attempted.current = true
    setBusy(true)
    setReading(true)
    try {
      const { data } = await api.post('/push/ai-action/in-app', { token: actions.token, action })
      if (mounted.current) setResult({ ok: data?.ok === true, message: data?.message || 'Karar sonucu doğrulanamadı. Sohbetten kontrol edin.' })
    } catch (error: any) {
      const status = error?.response?.status
      const message = status === 409 || status === 410
        ? 'Bu istek artık beklemiyor veya süresi dolmuş. Güncel durumu sohbetten kontrol edin.'
        : status === 403 ? 'Bu işlem için yetkiniz bulunmuyor.'
        : 'Kararın sonucu doğrulanamadı. Tekrar göndermeden sohbetten kontrol edin.'
      if (mounted.current) setResult({ ok: false, message })
    } finally { if (mounted.current) setBusy(false) }
  }

  return (
    <div role="status" aria-live="polite" aria-busy={busy} className={`app-toast notification-toast ${approval ? 'approval-toast' : ''} fixed z-[9400] flex w-[calc(100vw-1.5rem)] max-w-md flex-col overflow-hidden rounded-2xl border-2 ${border} bg-panel-card text-panel-text shadow-2xl`}
      onPointerEnter={() => setReading(true)} onPointerDown={() => setReading(true)} onFocusCapture={() => setReading(true)}>
      <div className={`flex shrink-0 items-start gap-3 p-3 ${background}`}>
        <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl border ${border} ${color}`} aria-label={label}><Icon size={22} aria-hidden="true" /></span>
        <div className="min-w-0 flex-1 self-center">
          <p aria-hidden="true" className={`mb-1 text-[11px] font-semibold ${color}`}>{label}</p>
          <time dateTime={new Date(createdAt).toISOString()} className="block text-[11px] text-panel-muted" title={new Date(createdAt).toLocaleString('tr-TR')}>{eventTime ? 'Bildirim zamanı' : 'Alınma zamanı'}: {new Date(createdAt).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</time>
          <button type="button" onClick={onOpen} disabled={busy} className="max-h-24 overflow-y-auto text-left text-sm font-semibold leading-5 [overflow-wrap:anywhere] disabled:opacity-60">{title}</button>
        </div>
        <button type="button" aria-label="Bildirimi kapat" disabled={busy} onClick={onDismiss} className="grid h-11 w-11 shrink-0 place-items-center rounded-xl text-panel-muted hover:bg-panel-card disabled:opacity-50"><X size={18} /></button>
      </div>
      <div tabIndex={0} aria-label="Bildirim metni" className="notification-toast-body min-h-0 overflow-y-auto overscroll-contain px-3 py-2 text-[13px] leading-relaxed [overflow-wrap:anywhere]">
        <NotificationBody body={body} accentClass={color} />
      </div>
      <div className="shrink-0 space-y-2 border-t border-panel-border p-3">
        {busy && <p className="flex items-center gap-2 text-xs text-panel-muted"><Loader2 size={14} className="animate-spin" />Karar gönderiliyor…</p>}
        {result && <p className={`text-xs leading-relaxed ${result.ok ? 'text-panel-success' : 'text-panel-warning'}`}>{result.message}</p>}
        {actions && !result && <div className="flex flex-wrap gap-2">
          {actions.actions.map(item => <button key={item.action} type="button" disabled={busy} onClick={() => void decide(item.action)}
            className={`flex min-h-11 min-w-0 flex-1 items-center justify-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold disabled:opacity-50 ${item.action === 'deny' ? 'border-panel-danger/40 bg-panel-danger/10 text-panel-danger hover:bg-panel-danger/20' : `${border} ${background} ${color} hover:brightness-110`}`}>
            {item.action === 'deny' ? <X size={16} className="shrink-0" /> : item.action === 'review' ? <ShieldAlert size={16} className="shrink-0" /> : <Check size={16} className="shrink-0" />}
            <span className="[overflow-wrap:anywhere]">{item.action === 'allow' ? 'Onayla' : item.title}</span>
          </button>)}
        </div>}
        <button type="button" disabled={busy} onClick={onOpen} className={`flex min-h-11 w-full items-center justify-between gap-2 rounded-xl px-3 py-2 text-left text-xs font-medium disabled:opacity-50 ${approval && !actions ? `${border} border ${background} ${color}` : 'text-panel-muted hover:bg-panel-hover'}`}>
          {result && !result.ok ? 'Sohbette kontrol et' : approval && !actions ? 'İncele ve onayla' : hasJob ? 'İş ayrıntısını aç' : 'İlgili bölümü aç'}<ChevronRight size={16} className="shrink-0" aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}
